import { ApiError, apiErrorFromResponse, apiFetch } from './http';
import { buildEnrollmentProductApiPath, buildPortalApiPath, buildSiteApiPath } from './routes';

export type ResultOutcome = 'success' | 'already_enrolled' | 'rejected' | 'validation_error' | 'ineligible' | 'system_error' | 'pending';

export interface SubmissionResultRecord {
  resultId: string;
  createdAt: string;
  updatedAt: string;
  siteType: string;
  appType: string;
  clientName: string;
  drugName: string;
  outcome: ResultOutcome;
  referenceIds?: Record<string, string>;
  detailType: string;
  detailVersion: number;
  details?: Record<string, unknown>;
}

export interface DraftSummary {
  resultId: string;
  createdAt: string;
  updatedAt: string;
  cardholderId?: string;
}

export interface DraftResponse {
  draftId: string;
  values: Record<string, unknown>;
}

export interface HistoryColumnHeader {
  label: string;
  type?: 'status' | string; // 'status' for status badge column, undefined for data columns
}

export interface SubmissionResultList {
  results: SubmissionResultRecord[];
  escalationStatuses?: Record<string, EscalationListStatus>;
  needsEnrichment?: boolean;
  historyColumns?: HistoryColumnHeader[];
  total: number;
  limit: number;
  offset: number;
  stepCounts?: Record<string, number>;
}

// EscalationListStatus is the status type embedded in list responses.
// Contains step/color for the badge plus config-driven column values for the
// history table. Column order matches escalations.pages.history.columns in the JSONC.
export interface EscalationListStatus {
  step: string;
  color: string;
  columns: FieldRow[];
}

// ── Status page types ──────────────────────────────────────────────────────────
//
// EscalationStatusPageData is the portal-owned response returned by the single
// escalation status endpoint. The backend shapes it from BenIQ data + the portal
// record according to escalations.pages.status in the client JSONC. The frontend
// renders it directly — no BenIQ field names are referenced here.

export interface FieldRow {
  label: string;
  value: string;
  span?: boolean;
  isPreview?: boolean;
  format?: string;
}

export interface EscalationTrackerData {
  step: string;
  color: string;
  isPreview?: boolean;
  inProgressDescription?: string;
  closedDescription?: string;
  /** When set, render this alert instead of the linear step indicator. */
  alertMessage?: string;
  alertColor?: string;
}

export interface EscalationOutcomeSection {
  result?: string;
  reason?: string;
  description?: string;
  resolvedAt?: string;
  color: string;
}

export interface EscalationActivityItem {
  title?: string;
  when?: string;
  detail?: string;
  outcome?: string;
  reason?: string;
  /** First name of the RIS agent who logged the event; set only when "agent" is configured. */
  agent?: string;
}

export interface EscalationComment {
  id: string;
  author?: string;  // Set by backend
  authorEmail?: string;  // Set by backend
  timestamp: string;
  text: string;
  reason?: string;
}

export interface EscalationAttachment {
  id: string;
  fieldId: string;
  fileName: string;
  fileSizeBytes: number;
  uploadedAt: string;
  uploadedBy?: string;
}

export interface EscalationStatusPageData {
  tracker: EscalationTrackerData;
  priority?: string;
  /** Present only when the escalation is flagged; reason is the agent's free text, if any. */
  transferToRisRx?: { reason?: string };
  submittedAt?: string;
  detailsLayoutStyle?: string;
  header: FieldRow[];
  details: FieldRow[];
  outcome?: EscalationOutcomeSection;
  activity?: EscalationActivityItem[];
  activityWhenDateOnly?: boolean;
  submittedBy?: FieldRow[];
  insurance?: FieldRow[];
  comments?: EscalationComment[];
  attachments?: EscalationAttachment[];
}

export function fetchSubmissionResult(siteType: string, resultId: string): Promise<SubmissionResultRecord> {
  return fetchSubmissionResultAtPath(buildSiteApiPath(siteType, `/results/${encodeURIComponent(resultId)}`));
}

export function fetchEnrollmentProductResult(product: string, resultId: string): Promise<SubmissionResultRecord> {
  return fetchSubmissionResultAtPath(buildEnrollmentProductApiPath(product, `/results/${encodeURIComponent(resultId)}`));
}

async function fetchSubmissionResultAtPath(path: string): Promise<SubmissionResultRecord> {
  const res = await apiFetch(path);

  if (res.status === 404) {
    throw new ApiError('Result not found', 404);
  }
  if (!res.ok) {
    throw new ApiError(`Failed to load result: ${res.status}`, res.status);
  }

  return res.json() as Promise<SubmissionResultRecord>;
}

export async function fetchDraft(siteType: string, draftId: string): Promise<DraftResponse> {
  const res = await apiFetch(buildPortalApiPath(`/${siteType}/drafts/${draftId}`));

  if (res.status === 404) {
    throw new ApiError('Draft not found', 404);
  }
  if (!res.ok) {
    throw new ApiError(`Failed to load draft: ${res.status}`, res.status);
  }

  return res.json() as Promise<DraftResponse>;
}

export interface SaveDraftResponse {
  draftId: string;
}

export async function saveDraft(siteType: string, values: Record<string, unknown>, draftId?: string): Promise<SaveDraftResponse> {
  const res = await apiFetch(buildPortalApiPath(`/${siteType}/drafts`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ draftId, values }),
  });

  if (!res.ok) {
    throw new ApiError(`Failed to save draft: ${res.status}`, res.status);
  }

  return res.json() as Promise<SaveDraftResponse>;
}

export async function listDrafts(siteType: string): Promise<DraftSummary[]> {
  const res = await apiFetch(buildPortalApiPath(`/${siteType}/drafts`));

  if (!res.ok) {
    throw new ApiError(`Failed to load drafts: ${res.status}`, res.status);
  }

  const body = await res.json() as { drafts: DraftSummary[] };
  return body.drafts;
}

export async function deleteDraft(siteType: string, draftId: string): Promise<void> {
  const res = await apiFetch(buildPortalApiPath(`/${siteType}/drafts/${draftId}`), {
    method: 'DELETE',
  });

  if (!res.ok) {
    throw new ApiError(`Failed to delete draft: ${res.status}`, res.status);
  }
}

export async function fetchResultsList(params: { 
  limit: number; 
  offset: number; 
  viewMine?: boolean; 
  search?: string; 
  status?: string;
  columnFilters?: Record<string, string[]>;
}): Promise<SubmissionResultList> {
  let url = buildPortalApiPath(`/escalations/results?limit=${params.limit}&offset=${params.offset}`);
  // Only include viewMine parameter when true (absence = view all)
  if (params.viewMine) url += '&viewMine=true';
  if (params.search && params.search.trim() !== '') url += `&q=${encodeURIComponent(params.search.trim())}`;
  if (params.status && params.status.trim() !== '' && params.status !== 'All') url += `&status=${encodeURIComponent(params.status.trim())}`;
  if (params.columnFilters) {
    for (const [columnName, values] of Object.entries(params.columnFilters)) {
      url += `&columnFilters[${encodeURIComponent(columnName)}]=${values.map(v => encodeURIComponent(v)).join(',')}`;
    }
  }
  
  const res = await apiFetch(url);

  if (res.status === 503) {
    throw new ApiError('Result history is currently unavailable', 503);
  }
  if (!res.ok) {
    throw new ApiError(`Failed to load result history: ${res.status}`, res.status);
  }

  return res.json() as Promise<SubmissionResultList>;
}

export async function fetchResultsEnrichment(resultIds: string[], viewMine?: boolean): Promise<Record<string, EscalationListStatus>> {
  let url = buildPortalApiPath(`/escalations/results/enrichment?ids=${resultIds.join(',')}`);
  // Only include viewMine parameter when true (absence = view all)
  if (viewMine) url += '&viewMine=true';
  const res = await apiFetch(url);

  if (res.status === 404 || res.status === 422 || res.status === 503) return {};
  if (!res.ok) {
    throw new ApiError(`Failed to enrich escalation results: ${res.status}`, res.status);
  }

  const body = await res.json() as { escalationStatuses: Record<string, EscalationListStatus> };
  return body.escalationStatuses ?? {};
}

export async function fetchEscalationStatus(siteType: string, resultId: string): Promise<EscalationStatusPageData> {
  const res = await apiFetch(buildPortalApiPath(`/${siteType}/results/${resultId}/status`));

  if (!res.ok) {
    throw new ApiError(`Failed to load escalation status: ${res.status}`, res.status);
  }

  return res.json() as Promise<EscalationStatusPageData>;
}

// Returns null when BenIQ has not produced a status yet (404, 422), throws on lookup failures.
export async function fetchEscalationStatusEnrichment(siteType: string, resultId: string): Promise<EscalationStatusPageData | null> {
  const res = await apiFetch(buildPortalApiPath(`/${siteType}/results/${resultId}/status/enrichment`));

  if (res.status === 404 || res.status === 422) {
    return null;
  }
  if (!res.ok) {
    throw new ApiError(`Failed to enrich escalation status: ${res.status}`, res.status);
  }

  return res.json() as Promise<EscalationStatusPageData>;
}

export async function postEscalationComment(siteType: string, resultId: string, text: string, authorName?: string): Promise<void> {
  const body: { text: string; authorName?: string } = { text };
  if (authorName) {
    body.authorName = authorName;
  }
  
  const res = await apiFetch(buildPortalApiPath(`/${siteType}/results/${resultId}/comments`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw await apiErrorFromResponse(res, `Failed to post comment: ${res.status}`);
  }
}